<template>
  <div :class="{ 'meta-layout': !$route.meta.layout && !$route.meta.noSidebar }">
    <custom-loading :show="$globalLoading.isLoading">
      <component
        :is="waterMarkComponent"
        v-if="waterMarkComponent"
        :userName="userName"
        :date="date"
        :ipAddress="ipAddress"
        :waterMarkKey="waterMarkKey"
      />
      <component :is="this.$route.meta.layout || 'div'">
        <router-view :key="$route.fullPath" />
      </component>
      <v-dialog v-model="showIdleWarning" v-if="showIdleWarning" max-width="420" persistent>
        <v-card>
          <v-card-title class="headline"> {{ localize("sessionTimeAboutToExpire") }} </v-card-title>
          <v-card-text>
            <div class="mb-2">{{ localize("youWillBeLoggedOut") }}</div>

            <div class="text--secondary">
              {{ localize("remainingTime") }}: <strong>{{ countdown }}</strong>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </custom-loading>
  </div>
</template>

<script>
import router from "./router";
import constant from "./main.constants";
import { loadComponent } from "@/helpers/separateLoaderEnvHelper";
import VueI18n from "@/plugins/vue-i18n";
import apicaller from "@/utils/apicaller";
import api from "./main.api";
export default {
  data() {
    return {
      clientSettings: {},
      countdown: 30,
      countdownInterval: null,
      date: "",
      idleLimit: null,
      idleTimer: null,
      ipAddress: "",
      showIdleWarning: false,
      isVisibilityChangedEventActive: false,
      userName: "",
      warningBefore: 30 * 1000,
      warningTimer: null,
      waterMarkKey: 0,
      watermarkTimer: null,
      waterMarkComponent: null,
    };
  },
  async created() {
    await this.loadWaterMarkComponent();
    if (this.waterMarkComponent) {
      this.$eventBus.$on("setWatermark", this.setWatermark);
      this.setWatermark();
      this.watermarkTimer = setInterval(() => {
        this.date = new Date().toLocaleString();
        this.waterMarkKey++;
      }, 60000);
    }
    this.$eventBus.$on("setIdleTimeout", this.setIdleTimeout);

    this.updatePageDirection();
  },
  beforeDestroy() {
    this.disposeWatermarkTimer();
    this.destroyActionListeners();
  },
  methods: {
    async loadWaterMarkComponent() {
      try {
        const component = await loadComponent(constant.ENV_COMPONENTS.APP_WATERMARK);
        if (component) {
          this.waterMarkComponent = component.default || component;
        }
      } catch (error) {
        console.error("WaterMark component yüklenemedi:", error);
      }
    },
    disposeWatermarkTimer() {
      if (this.watermarkTimer != null) {
        clearInterval(this.watermarkTimer);
        this.watermarkTimer = null;
      }
      if (this.$eventBus && typeof this.$eventBus.$off === "function") {
        this.$eventBus.$off("setWatermark", this.setWatermark);
      }
    },
    updatePageDirection() {
      const language = localStorage.getItem("preferredLanguage");
      if (language && language == "ar") {
        this.$vuetify.rtl = true;
      } else {
        this.$vuetify.rtl = false;
      }
    },
    decodeBase64ToUTF8(token) {
      const tokenPayload = token.split(".")[1];
      const decodedPayload = decodeURIComponent(escape(atob(tokenPayload)));
      const tokenInfoMap = JSON.parse(decodedPayload);
      return tokenInfoMap;
    },
    setWatermark() {
      if (localStorage.access_token) {
        const tokenInfoMap = this.decodeBase64ToUTF8(localStorage.access_token);
        this.ipAddress = tokenInfoMap.ipAddress.split(":").pop();
        this.userName = tokenInfoMap.UserName || "Watermark";
        this.date = new Date().toLocaleString();
      }
    },
    setIdleTimeout() {
      if (localStorage.clientSettings) {
        this.clientSettings = JSON.parse(localStorage.clientSettings);
        if (this.clientSettings.IdleTimeout) {
          if (!this.isVisibilityChangedEventActive) {
            document.addEventListener("visibilitychange", this.visibilityChanged);
            this.isVisibilityChangedEventActive = true;
            this.prepareIdleLimit();
          }
          this.createActionListeners();
        }
      }
    },
    prepareIdleLimit() {
      if (this.clientSettings.IdleTimeout) {
        this.idleLimit = this.clientSettings.IdleTimeout;
      } else {
        this.idleLimit = null;
      }
    },
    createActionListeners() {
      const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
      events.forEach((e) => window.addEventListener(e, this.handleUserActivity));

      this.startIdleTimers();
    },
    startIdleTimers() {
      if (this.idleLimit) {
        this.clearIdleTimers();

        this.warningTimer = setTimeout(() => {
          this.openWarning();
        }, this.idleLimit - this.warningBefore);

        this.idleTimer = setTimeout(() => {
          this.logOutUser();
        }, this.idleLimit);
      }
    },
    visibilityChanged() {
      if (!document.hidden) {
        this.createActionListeners();
      } else {
        this.showIdleWarning = false;
        this.clearIdleTimers();
      }
    },
    openWarning() {
      this.showIdleWarning = true;
      this.countdown = 30;

      this.countdownInterval = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.countdownInterval);
        }
      }, 1000);
    },
    logOutUser() {
      this.showIdleWarning = false;
      apicaller.delete(api.AUTH.deleteUserTokenOnLogout, { params: { reason: "idle" } })
        .then(() => this.logOut())
        .catch((err) => {
          console.error("Token silme hatası:", err);
          this.errorHandler("logoutFailed");
        });
    },
    clearIdleTimers() {
      clearTimeout(this.warningTimer);
      clearTimeout(this.idleTimer);
      clearInterval(this.countdownInterval);
    },
    handleUserActivity() {
      if (this.showIdleWarning) {
        this.showIdleWarning = false;
      }

      this.startIdleTimers();
    },
    destroyActionListeners() {
      const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
      events.forEach((e) => window.removeEventListener(e, this.handleUserActivity));
      document.removeEventListener("visibilitychange", this.visibilityChanged);
      this.isVisibilityChangedEventActive = false;
      this.clearIdleTimers();
    },
    logOut(isTokenExpired) {
      const localStorageItems = constant.LOCAL_STORAGE_ITEMS;
      const sessionStorageItems = constant.SESSION_STORAGE_ITEMS;

      Object.keys(localStorageItems).forEach((item) => {
        if (!constant.DONT_REMOVE_LOCALE_STORAGE_ITEMS[item]) {
          localStorage.removeItem(localStorageItems[item]);
        }
      });

      Object.keys(sessionStorageItems).forEach((item) => {
        sessionStorage.removeItem(sessionStorageItems[item]);
      });
      if (router.history.current.path != "/login") {
        const loginHash = isTokenExpired ? "#/login?type=tokenExpired" : "#/login";
        window.location.hash = loginHash;
        window.location.reload();
      }
    },
    // bu fonksiyon commonFunc burada import edildiğinde circular dependency olduğu için burada yeniden yazıldı
    localize(localizeKey) {
      if (VueI18n.te("KEYS." + localizeKey)) {
        return VueI18n.t("KEYS." + localizeKey);
      } else {
        return localizeKey;
      }
    },
  },
};
</script>
<style lang="scss">
@import "./main.scss";

/* font-display: swap yerine block. swap ile metin once yedek fontla cizilip
   web fontu inince degistiriliyor; .ttf dosyalari ~97KB oldugu icin bu takas
   ilk yuklemede (cache yokken) gozle gorulur bir font degisimi olarak
   yasaniyordu. block kisa bir sure metni gizler, yanlis fontla cizmez. */
@font-face {
  font-family: "Manrope";
  src: url("~@/assets/fonts/Manrope/Manrope-ExtraLight.ttf") format("truetype");
  font-weight: 200;
  font-style: normal;
  font-display: block;
}

@font-face {
  font-family: "Manrope";
  src: url("~@/assets/fonts/Manrope/Manrope-Light.ttf") format("truetype");
  font-weight: 300;
  font-style: normal;
  font-display: block;
}

@font-face {
  font-family: "Manrope";
  src: url("~@/assets/fonts/Manrope/Manrope-Regular.ttf") format("truetype");
  font-weight: 400;
  font-style: normal;
  font-display: block;
}

@font-face {
  font-family: "Manrope";
  src: url("~@/assets/fonts/Manrope/Manrope-Medium.ttf") format("truetype");
  font-weight: 500;
  font-style: normal;
  font-display: block;
}

@font-face {
  font-family: "Manrope";
  src: url("~@/assets/fonts/Manrope/Manrope-SemiBold.ttf") format("truetype");
  font-weight: 600;
  font-style: normal;
  font-display: block;
}

@font-face {
  font-family: "Manrope";
  src: url("~@/assets/fonts/Manrope/Manrope-Bold.ttf") format("truetype");
  font-weight: 700;
  font-style: normal;
  font-display: block;
}

@font-face {
  font-family: "Manrope";
  src: url("~@/assets/fonts/Manrope/Manrope-ExtraBold.ttf") format("truetype");
  font-weight: 800;
  font-style: normal;
  font-display: block;
}

@font-face {
  font-family: "Plus Jakarta Sans";
  src: url("~@/assets/fonts/PlusJakartaSans/PlusJakartaSans-ExtraLight.ttf") format("truetype");
  font-weight: 200; // ExtraLight
  font-style: normal;
  font-display: block;
}

@font-face {
  font-family: "Plus Jakarta Sans";
  src: url("~@/assets/fonts/PlusJakartaSans/PlusJakartaSans-ExtraLightItalic.ttf") format("truetype");
  font-weight: 200;
  font-style: italic;
  font-display: block;
}

@font-face {
  font-family: "Plus Jakarta Sans";
  src: url("~@/assets/fonts/PlusJakartaSans/PlusJakartaSans-Light.ttf") format("truetype");
  font-weight: 300; // Light
  font-style: normal;
  font-display: block;
}

@font-face {
  font-family: "Plus Jakarta Sans";
  src: url("~@/assets/fonts/PlusJakartaSans/PlusJakartaSans-LightItalic.ttf") format("truetype");
  font-weight: 300;
  font-style: italic;
  font-display: block;
}

@font-face {
  font-family: "Plus Jakarta Sans";
  src: url("~@/assets/fonts/PlusJakartaSans/PlusJakartaSans-Regular.ttf") format("truetype");
  font-weight: 400; // Regular
  font-style: normal;
  font-display: block;
}

@font-face {
  font-family: "Plus Jakarta Sans";
  src: url("~@/assets/fonts/PlusJakartaSans/PlusJakartaSans-Italic.ttf") format("truetype"); // Not regular italic, but usually maps to 400
  font-weight: 400;
  font-style: italic;
  font-display: block;
}

@font-face {
  font-family: "Plus Jakarta Sans";
  src: url("~@/assets/fonts/PlusJakartaSans/PlusJakartaSans-Medium.ttf") format("truetype");
  font-weight: 500; // Medium
  font-style: normal;
  font-display: block;
}

@font-face {
  font-family: "Plus Jakarta Sans";
  src: url("~@/assets/fonts/PlusJakartaSans/PlusJakartaSans-MediumItalic.ttf") format("truetype");
  font-weight: 500;
  font-style: italic;
  font-display: block;
}

@font-face {
  font-family: "Plus Jakarta Sans";
  src: url("~@/assets/fonts/PlusJakartaSans/PlusJakartaSans-SemiBold.ttf") format("truetype");
  font-weight: 600; // SemiBold
  font-style: normal;
  font-display: block;
}

@font-face {
  font-family: "Plus Jakarta Sans";
  src: url("~@/assets/fonts/PlusJakartaSans/PlusJakartaSans-SemiBoldItalic.ttf") format("truetype");
  font-weight: 600;
  font-style: italic;
  font-display: block;
}

@font-face {
  font-family: "Plus Jakarta Sans";
  src: url("~@/assets/fonts/PlusJakartaSans/PlusJakartaSans-Bold.ttf") format("truetype");
  font-weight: 700; // Bold
  font-style: normal;
  font-display: block;
}

@font-face {
  font-family: "Plus Jakarta Sans";
  src: url("~@/assets/fonts/PlusJakartaSans/PlusJakartaSans-BoldItalic.ttf") format("truetype");
  font-weight: 700;
  font-style: italic;
  font-display: block;
}

@font-face {
  font-family: "Plus Jakarta Sans";
  src: url("~@/assets/fonts/PlusJakartaSans/PlusJakartaSans-ExtraBold.ttf") format("truetype");
  font-weight: 800; // ExtraBold
  font-style: normal;
  font-display: block;
}

@font-face {
  font-family: "Plus Jakarta Sans";
  src: url("~@/assets/fonts/PlusJakartaSans/PlusJakartaSans-ExtraBoldItalic.ttf") format("truetype");
  font-weight: 800;
  font-style: italic;
  font-display: block;
}

#app {
  font-family: "Plus Jakarta Sans", Arial, sans-serif; /* Burayı güncelledim */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
}

.meta-layout {
  margin-left: 45px;
}
</style>
